import { useLocation } from "react-router-dom";
import * as diacritics from "diacritics";
import { colors } from "./components/colors";
import moment from "moment";
import { localStorageCountrySpecificValues } from "./module/common/OrganizationTopbarSection";
import { CollectionMethod } from "./module/shop/model/ShopSuccessResponse";
import { config } from "./config";
import { AutoCampaignListItem } from "./module/auto-campaigns/campaigns/models/AutoCampaign";
import { MenuCampaign } from "./module/menus/models/Menus";
import { OrganizationKeyName } from "./model/Organization";

export type CountrySpecificValues = {
  countryCode: string;
  currency: string;
  vatPercentage: number;
  locale: string;
  timezone: string;
};

export const getCountrySpecificValues = (): CountrySpecificValues => {
  const countrySpecificValues = localStorage.getItem(
    localStorageCountrySpecificValues
  );
  if (countrySpecificValues) {
    return JSON.parse(countrySpecificValues);
  } else {
    return {
      countryCode: "DK",
      currency: "DKK",
      locale: "da",
      vatPercentage: 25,
      timezone: "Europe/Copenhagen",
    };
  }
};

export const currencyDecimals = new Map([["ISK", 0]]);

export const getCurrencyDecimals = (currency: string) => {
  return currencyDecimals.get(currency) ?? 2;
};

export function formatMoneyAmount(amount: number, currency: string) {
  const decimals = getCurrencyDecimals(currency);
  const price = amount / Math.pow(10, decimals);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(price);
}

export const getFormattedCurrency = (
  amount: number,
  currency: string,
  locale: "dk" | "en" = "en"
) => {
  const l = new Intl.Locale(locale);
  const localeString = l.baseName;
  return new Intl.NumberFormat(localeString, {
    style: "currency",
    currency,
  })
    .formatToParts(amount)
    .filter((x) => x.type === "currency")
    .map((x) => x.value)
    .join("");
};

export const getFormattedMoneyAmountNumber = (
  amount: number,
  currency: string
) => {
  const decimals = getCurrencyDecimals(currency);
  const price = amount / Math.pow(10, decimals);
  const locale = new Intl.Locale("en");
  const localeString = locale.baseName;
  return new Intl.NumberFormat(localeString, {
    style: "currency",
    currency,
  })
    .formatToParts(price)
    .filter((x) => x.type !== "currency")
    .filter((x) => x.type !== "literal" || x.value.trim().length !== 0)
    .map((x) => x.value)
    .join("");
};

export const transformPriceFromAPI = (price: number, currency?: string) => {
  let decimals = 2;
  if (currency) {
    decimals = getCurrencyDecimals(currency);
  }
  return price / Math.pow(10, decimals);
};

type OrderType = {
  orderType: string;
  finalState: string;
};

export const formatOrderType = (orderType: string): OrderType => {
  switch (orderType) {
    case "pickup":
      return {
        orderType: "Pick up",
        finalState: "Picked up",
      };
    case "delivery":
      return {
        orderType: "Delivery",
        finalState: "Delivered",
      };
    case "eat-in":
      return {
        orderType: "Eat-in",
        finalState: "Delivered",
      };
    case "none":
      return {
        orderType: "Digital",
        finalState: "Delivered",
      };
    default:
      return {
        orderType: orderType,
        finalState: orderType,
      };
  }
};

export const formatOrderState = (
  orderState: string,
  orderType: string
): string => {
  if (orderType === "none") {
    return "Digital";
  }

  switch (orderState) {
    case "new":
      return "Selecting products";
    case "in_checkout":
      return "Currently paying";
    case "paid":
    case "placed":
      return "Awaiting restaurant accept";
    case "accepted":
    case "manufacturing":
      return "Cooking in restaurant";
    case "accepted.pre_order":
      return "Pre-order accepted";
    case "ready":
      return "Ready for pick up";
    case "in_delivery":
      return "In delivery";
    case "finalized":
    case "delivered":
      return formatOrderType(orderType).finalState;
    case "rejected":
      return "Rejected";
    default:
      return orderState;
  }
};

export const formatOrderStateBgColor = (orderState: string): string => {
  switch (orderState) {
    case "new":
    case "in_checkout":
    case "paid":
      return colors.ctaInactive;
    case "accepted":
    case "manufacturing":
    case "accepted.pre_order":
    case "ready":
    case "in_delivery":
      return colors.ctaLight;
    case "finalized":
    case "delivered":
      return colors.successLight;
    case "rejected":
      return colors.errorLight;
    default:
      return colors.ctaInactive;
  }
};

export const formatOrderStateTextColor = (orderState: string): string => {
  switch (orderState) {
    case "new":
    case "in_checkout":
    case "paid":
      return colors.textPrimary;
    case "accepted":
    case "manufacturing":
    case "accepted.pre_order":
    case "ready":
    case "in_delivery":
      return colors.ctaMain;
    case "finalized":
    case "delivered":
      return colors.successDark;
    case "rejected":
      return colors.errorDark;
    default:
      return colors.textPrimary;
  }
};

export const formatDeliveryProviderName = (name: string) => {
  switch (name) {
    case "woltdrive":
      return "Wolt Drive";
    case "rider":
      return "Rider";
    case "foodora_go":
      return "Foodora Go";
    default:
      return name;
  }
};

export const useIsOrganization = (orgKeyName: string[]) => {
  const location = useLocation();
  const isOrganization = orgKeyName.some((org) =>
    location.pathname.includes(org)
  );

  return isOrganization;
};

export const orgsWithAutoCampaigns = ["noahs", "shell"];

export const useIsWarner = () => {
  const location = useLocation();
  const isWarner = location.pathname.includes("warner-music");
  return isWarner;
};

export const useIsBones = () => {
  const location = useLocation();
  const isWarner = location.pathname.includes("bones");
  return isWarner;
};

export const useIsCustomer = (organizationKeyName: OrganizationKeyName) => {
  const location = useLocation();
  const isCustomer = location.pathname.includes(organizationKeyName);
  return isCustomer;
};

export const useIsRekom = () => {
  const location = useLocation();
  const isRekom = location.pathname.includes("rekom");
  return isRekom;
};

export const formatDate = (dateString: string) => {
  return moment(dateString).format("MMM D");
};

export const formatDateWithTime = (dateString: string) => {
  return moment(dateString).format("MMM D, HH:mm");
};

export const formatDateWithYear = (dateString: string) => {
  return moment(dateString).format("MMM D, YYYY");
};

export const formatDateWithYearAndTime = (dateString: string) => {
  return moment(dateString).format("MMM D, YYYY, HH:mm");
};

export const formatTimeRange = (start: string, end: string) => {
  if (start === "00:00:00" && end === "00:00:00") {
    return "All day";
  }
  const startComponents = start.split(":");
  const endComponents = end.split(":");

  if (startComponents.length === 2 && endComponents.length === 2) {
    return `${startComponents[0]}:${startComponents[1]} - ${endComponents[0]}:${endComponents[1]}`;
  }

  if (startComponents.length === 3 && endComponents.length === 3) {
    let start = `${startComponents[0]}:${startComponents[1]}`;
    let end = `${endComponents[0]}:${endComponents[1]}`;
    if (startComponents[2] !== "00") {
      start += `:${startComponents[2]}`;
    }
    if (endComponents[2] !== "00") {
      end += `:${endComponents[2]}`;
    }
    return `${start} - ${end}`;
  }

  return `${start} - ${end}`;
};

export const useIsUsingSharedPrice = () => {
  const location = useLocation();
  const hostname = window ? window.location.hostname : undefined;
  // If staging or local, always return true
  if (hostname?.includes("admin-staging") || hostname?.includes("localhost")) {
    return true;
  }

  // Enter org names here that should use shared option price
  const orgsUsingSharedPrice: string[] = ["bones", "jensens"];
  const isUsingSharedPrice = orgsUsingSharedPrice.some((org) =>
    location.pathname.includes(org)
  );
  return isUsingSharedPrice;
};

// Generated by Chat GPT
export const europeanCountryFlags = new Map([
  ["AL", "🇦🇱"],
  ["AD", "🇦🇩"],
  ["AM", "🇦🇲"],
  ["AT", "🇦🇹"],
  ["AZ", "🇦🇿"],
  ["BY", "🇧🇾"],
  ["BE", "🇧🇪"],
  ["BA", "🇧🇦"],
  ["BG", "🇧🇬"],
  ["HR", "🇭🇷"],
  ["CY", "🇨🇾"],
  ["CZ", "🇨🇿"],
  ["DK", "🇩🇰"],
  ["EE", "🇪🇪"],
  ["FI", "🇫🇮"],
  ["FR", "🇫🇷"],
  ["GE", "🇬🇪"],
  ["DE", "🇩🇪"],
  ["GR", "🇬🇷"],
  ["HU", "🇭🇺"],
  ["IS", "🇮🇸"],
  ["IE", "🇮🇪"],
  ["IT", "🇮🇹"],
  ["KZ", "🇰🇿"],
  ["LV", "🇱🇻"],
  ["LI", "🇱🇮"],
  ["LT", "🇱🇹"],
  ["LU", "🇱🇺"],
  ["MK", "🇲🇰"],
  ["MT", "🇲🇹"],
  ["MD", "🇲🇩"],
  ["MC", "🇲🇨"],
  ["ME", "🇲🇪"],
  ["NL", "🇳🇱"],
  ["NO", "🇳🇴"],
  ["PL", "🇵🇱"],
  ["PT", "🇵🇹"],
  ["RO", "🇷🇴"],
  ["RU", "🇷🇺"],
  ["SM", "🇸🇲"],
  ["RS", "🇷🇸"],
  ["SK", "🇸🇰"],
  ["SI", "🇸🇮"],
  ["ES", "🇪🇸"],
  ["SE", "🇸🇪"],
  ["CH", "🇨🇭"],
  ["TR", "🇹🇷"],
  ["UA", "🇺🇦"],
  ["GB", "🇬🇧"],
  ["VA", "🇻🇦"],
  ["XK", "🇽🇰"],
  ["BA", "🇧🇦"],
  ["GL", "🇬🇱"],
]);

export const sanitizeString = (string: string) => {
  return diacritics.remove(string.toLowerCase().replace(/\W+/g, ""));
};

export const minutesToSeconds = (minutes: number) => {
  return minutes * 60;
};

export const secondsToMinutes = (seconds: number) => {
  return seconds / 60;
};

export const getCollectionMethodLabel = (
  collectionMethod: CollectionMethod
) => {
  switch (collectionMethod) {
    case CollectionMethod.PICKUP:
      return "Pick-up";
    case CollectionMethod.EAT_IN:
      return "Eat-in";
    case CollectionMethod.COURIER:
      return "Delivery";
    default:
      return collectionMethod;
  }
};

export const formatDateForAPI = (dateString: string) => {
  return moment(dateString).format("YYYY-MM-DD");
};
export const isStaging = config.environment === "staging";

export const getAutoCampaignStatus = (
  autoCampaign: AutoCampaignListItem | MenuCampaign
) => {
  if (!autoCampaign.enabled) {
    return "disabled";
  }

  const today = moment();

  const isActive = today.isSameOrBefore(moment(autoCampaign.activeTo));

  if (!isActive) {
    return "expired";
  }

  return "enabled";
};

export const ORDER_FILTER_STATES = [
  "accepted",
  "accepted.pre_order",
  "delivered",
  "in_delivery",
  "manufacturing",
  "paid",
  "placed",
  "ready",
  "rejected",
];
